import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  editClient,
  getAllClients,
  getAttribution,
  getHereDotComAccounts,
  saveAttribution,
  indexClient,
} from "../Services/API/ClientManagement";
import {
  Client,
  ClientAttributionConfiguration,
  EditClient,
  HereDotcomAccount,
} from "../Types/ClientManagement";

export const useAllClientsQuery = <T = Client[]>(
  enabled: boolean = true,
  select?: (data: Client[]) => T | undefined,
) => {
  return useQuery(["allCareersClients"], () => getAllClients(), {
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
    select,
  });
};

export const useCareersClientQuery = (clientId: string) => {
  return useAllClientsQuery(!!clientId, (data) => data.find((client) => client.id === clientId));
};

export const useEditClientMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ clientId, editedClient }: { clientId: string; editedClient: EditClient }) =>
      editClient(clientId, editedClient),
    {
      mutationKey: ["editClient"],
      onSuccess: () => {
        queryClient.refetchQueries(["allCareersClients"]);
      },
    },
  );
};

export const defaultHereDotComAccount = { name: "None", id: "None" } as HereDotcomAccount;

export const useHereDotComAccountsQuery = () => {
  return useQuery(
    ["hereDotComAccounts"],
    async () => {
      const allAccounts = await getHereDotComAccounts();
      return {
        hereDotComBetaValues: [
          defaultHereDotComAccount,
          ...allAccounts
            .filter((account) => !account.isProduction)
            .sort((a, b) => a.name.localeCompare(b.name)),
        ],
        hereDotComProdValues: [
          defaultHereDotComAccount,
          ...allAccounts
            .filter((account) => account.isProduction)
            .sort((a, b) => a.name.localeCompare(b.name)),
        ],
      } as { hereDotComBetaValues: HereDotcomAccount[]; hereDotComProdValues: HereDotcomAccount[] };
    },
    { staleTime: 1000 * 60 * 15 }, // 15 minutes
  );
};

export const useClientAttributionQuery = (clientId: string) => {
  return useQuery(["clientAttribution", { clientId }], () => getAttribution(clientId), {
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!clientId,
  });
};

export const useSaveClientAttributionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation((attribution: ClientAttributionConfiguration) => saveAttribution(attribution), {
    mutationKey: ["saveClientAttribution"],
    onSettled: (savedConfig) => {
      if (savedConfig && savedConfig.clientId) {
        // Set the new data in the cache since the API returns the updated object, no need to refetch
        queryClient.setQueryData(["clientAttribution", { clientId: savedConfig.clientId }], savedConfig);
      }
    },
  });
};

export const useIndexClientMutation = () => {
  return useMutation((clientId: string) => indexClient(clientId), {
    mutationKey: ["indexClient"],
  });
};
